import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import { CreditCard, CheckCircle2, AlertTriangle, Clock, Receipt as ReceiptIcon } from "lucide-react";

const STATUS_LABELS = {
  trialing: { label: "Periodo di prova", icon: Clock, color: "bg-blue-50 border-blue-200 text-blue-900" },
  active: { label: "Attivo", icon: CheckCircle2, color: "bg-green-50 border-green-200 text-green-900" },
  past_due: { label: "Pagamento scaduto", icon: AlertTriangle, color: "bg-red-50 border-red-200 text-red-900" },
  canceled: { label: "Annullato", icon: AlertTriangle, color: "bg-slate-100 border-slate-300 text-slate-700" },
};

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, tx] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/transactions"),
      ]);
      setStatus(s.data);
      setTransactions(tx.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // PayPal availability probe — hides the button when backend isn't configured
  useEffect(() => {
    api.get("/paypal/config")
      .then(({ data }) => setPaypalEnabled(Boolean(data?.enabled)))
      .catch(() => setPaypalEnabled(false));
  }, []);

  // Gestisce il ritorno da PayPal (?paypal=success|failed|cancelled)
  useEffect(() => {
    const paypalResult = searchParams.get("paypal");
    if (!paypalResult) return;
    if (paypalResult === "success") {
      toast.success("Pagamento PayPal ricevuto — abbonamento attivo!");
      load();
    } else if (paypalResult === "cancelled") {
      toast.info("Pagamento PayPal annullato");
    } else if (paypalResult === "failed") {
      toast.error("Pagamento PayPal fallito — riprova");
    }
    setSearchParams({});
  }, [searchParams, setSearchParams, load]);

  // Gestisce il ritorno da Stripe (?session_id=...) con polling
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      if (cancelled || attempts >= maxAttempts) {
        if (attempts >= maxAttempts) toast.error("Timeout verifica pagamento — riprova");
        return;
      }
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Pagamento ricevuto — abbonamento attivo!");
          setSearchParams({});
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Sessione di pagamento scaduta");
          setSearchParams({});
          return;
        }
      } catch (err) {
        // Network blip during polling: ignored on purpose, the next attempt
        // (setTimeout 2s) will retry. After `maxAttempts` we surface a toast.
        logDev("billing: poll retry", err);
      }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, load]);

  const onPay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore checkout");
      setLoading(false);
    }
  };

  const onPayPaypal = async () => {
    setPaypalLoading(true);
    try {
      const { data } = await api.post("/paypal/create-order");
      if (!data?.approval_url) throw new Error("Missing approval URL");
      window.location.href = data.approval_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore PayPal");
      setPaypalLoading(false);
    }
  };

  if (!status) return <div className="p-12 text-slate-500">Caricamento…</div>;

  const st = STATUS_LABELS[status.status] || STATUS_LABELS.trialing;
  const StIcon = st.icon;
  const b = status.billing;
  const trialEnd = status.trial_end_at ? new Date(status.trial_end_at) : null;
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;

  return (
    <div className="p-6 md:p-12 max-w-5xl">
      <p className="text-xs tracked text-blue-700 mb-2">ABBONAMENTO</p>
      <h1 className="font-heading font-semibold text-4xl tracking-tight mb-8" data-testid="billing-title">Fatturazione</h1>

      {/* Status card */}
      <div className={`border p-6 mb-6 flex items-start gap-4 ${st.color}`} data-testid="billing-status-card">
        <StIcon className="w-6 h-6 flex-shrink-0 mt-1" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-heading font-semibold text-2xl">{st.label}</div>
          {status.status === "trialing" && (
            <p className="text-sm mt-1">
              Periodo di prova gratuito — termina il {trialEnd?.toLocaleDateString("it-IT")} ({trialDaysLeft} giorni rimanenti)
            </p>
          )}
          {status.status === "active" && status.last_payment_at && (
            <p className="text-sm mt-1">
              Ultimo pagamento: {new Date(status.last_payment_at).toLocaleDateString("it-IT")}
            </p>
          )}
          {status.status === "past_due" && (
            <p className="text-sm mt-1">
              Il tuo trial è terminato. Regolarizza il pagamento per continuare a usare Xenia. L'account sarà bloccato dopo 5 giorni dalla scadenza.
            </p>
          )}
        </div>
      </div>

      {/* Invoice preview */}
      <div className="border border-slate-200 bg-white p-6 mb-6" data-testid="billing-invoice-preview">
        <div className="flex items-center gap-2 mb-4">
          <ReceiptIcon className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
          <h2 className="font-heading font-semibold text-xl">Fattura in maturazione · periodo corrente</h2>
        </div>
        <table className="w-full text-sm mb-4">
          <tbody>
            <Row label={`Abbonamento strutture (${b.properties} × € ${b.price_per_property.toFixed(2)})`} value={`€ ${b.subscription_amount.toFixed(2)}`} />
            <Row label={`Prenotazioni in questo ciclo (${b.bookings} × € ${b.price_per_booking.toFixed(2)})`} value={`€ ${b.usage_amount.toFixed(2)}`} />
            <tr className="border-t-2 border-slate-300">
              <td className="py-3 font-heading font-semibold text-lg">Totale dovuto</td>
              <td className="py-3 text-right font-heading font-semibold text-2xl" data-testid="billing-total">€ {b.total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        <p className="text-xs text-slate-500 mb-4">
          Il totale sarà addebitato al termine del ciclo mensile. Puoi anticipare il pagamento in qualsiasi momento.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onPay}
            disabled={loading || b.total <= 0}
            data-testid="billing-pay-now"
            className="inline-flex items-center justify-center gap-2 bg-blue-700 text-stone-50 px-5 py-3 rounded-lg text-sm hover:bg-blue-800 disabled:opacity-60"
          >
            <CreditCard className="w-4 h-4" />
            {loading ? "Apertura checkout…" : `Paga con carta · € ${b.total.toFixed(2)}`}
          </button>
          {paypalEnabled && (
            <button
              onClick={onPayPaypal}
              disabled={paypalLoading || b.total <= 0}
              data-testid="billing-pay-paypal"
              className="inline-flex items-center justify-center gap-2 bg-[#ffc439] text-[#003087] px-5 py-3 rounded-lg text-sm font-semibold hover:bg-[#f5b800] disabled:opacity-60"
            >
              <span className="font-bold tracking-tight">Pay<span className="italic">Pal</span></span>
              {paypalLoading ? "Reindirizzamento…" : `· € ${b.total.toFixed(2)}`}
            </button>
          )}
        </div>
      </div>

      {/* Transaction history */}
      <div className="border border-slate-200 bg-white p-6" data-testid="billing-transactions">
        <h2 className="font-heading font-semibold text-xl mb-4">Storico pagamenti</h2>
        {transactions.length === 0 ? (
          <p className="text-slate-500 text-sm">Nessun pagamento ancora effettuato.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2">Data</th>
                <th className="text-right">Importo</th>
                <th className="text-right">Stato</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.session_id} className="border-b border-stone-100">
                  <td className="py-2">{new Date(t.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="py-2 text-right">€ {t.amount.toFixed(2)}</td>
                  <td className="py-2 text-right">
                    <span className={`text-xs px-2 py-1 ${t.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>
                      {t.payment_status === "paid" ? "Pagato" : t.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="py-3 text-slate-700">{label}</td>
      <td className="py-3 text-right font-medium">{value}</td>
    </tr>
  );
}
